import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Download,
  Presentation,
  Loader2,
  FileText,
  Image,
  Maximize,
  Minimize,
  Quote,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDocumentGenerator, DocumentContent, DocumentSection } from '@/hooks/useDocumentGenerator';
import { usePptxGenerator } from '@/hooks/usePptxGenerator';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import type { SlidesDeck, Slide } from '@/lib/content-studio/types';

interface SlidesViewerProps {
  slides: SlidesDeck;
  className?: string;
}

// ─── Slide Layout Renderers ────────────────────────────────────────────────────

function TitleSlideLayout({ slide, primaryColor }: { slide: Slide; primaryColor: string }) {
  return (
    <div className="flex flex-col h-full">
      {/* Gradient accent bar */}
      <div
        className="h-2 w-full rounded-t-sm"
        style={{
          background: `linear-gradient(90deg, ${primaryColor}, ${primaryColor}88)`,
        }}
      />
      <div className="flex-1 flex flex-col items-center justify-center px-12 py-8">
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 text-center leading-tight mb-4">
          {slide.title}
        </h1>
        {slide.content?.mainText && (
          <p className="text-lg sm:text-xl text-slate-500 text-center max-w-2xl mx-auto">
            {slide.content.mainText}
          </p>
        )}
        {slide.content?.bullets && slide.content.bullets.length > 0 && (
          <p className="text-base text-slate-400 text-center mt-2">
            {slide.content.bullets[0]}
          </p>
        )}
      </div>
      {/* Bottom accent line */}
      <div
        className="h-1 w-32 mx-auto mb-6 rounded-full"
        style={{ backgroundColor: primaryColor }}
      />
    </div>
  );
}

function ContentSlideLayout({ slide, primaryColor }: { slide: Slide; primaryColor: string }) {
  return (
    <div className="flex flex-col h-full px-10 py-7">
      {/* Title bar */}
      <div className="mb-5 pb-3 border-b-2" style={{ borderColor: `${primaryColor}40` }}>
        <h2 className="text-2xl font-bold text-slate-900">{slide.title}</h2>
      </div>
      {/* Body — vertically centred */}
      <div className="flex-1 flex flex-col justify-center overflow-hidden">
        {slide.content?.mainText && (
          <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-line">
            {slide.content.mainText}
          </p>
        )}
        {slide.content?.bullets && slide.content.bullets.length > 0 && (
          <ul className="mt-4 space-y-3">
            {slide.content.bullets.map((bullet, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span
                  className="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: primaryColor }}
                />
                <span className="text-base text-slate-700">{bullet}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function BulletsSlideLayout({ slide, primaryColor }: { slide: Slide; primaryColor: string }) {
  return (
    <div className="flex flex-col h-full px-10 py-7">
      <div className="mb-5 pb-3 border-b-2" style={{ borderColor: `${primaryColor}40` }}>
        <h2 className="text-2xl font-bold text-slate-900">{slide.title}</h2>
      </div>
      <div className="flex-1 flex flex-col justify-center overflow-hidden">
        {slide.content?.mainText && (
          <p className="text-sm text-slate-500 mb-5">{slide.content.mainText}</p>
        )}
        <ul className="space-y-4">
          {(slide.content?.bullets ?? []).map((bullet, idx) => (
            <li key={idx} className="flex items-start gap-4">
              <span
                className="mt-1.5 w-3 h-3 rounded-full flex-shrink-0 shadow-sm"
                style={{ backgroundColor: primaryColor }}
              />
              <span className="text-lg text-slate-700 leading-relaxed">{bullet}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function TwoColumnSlideLayout({ slide, primaryColor }: { slide: Slide; primaryColor: string }) {
  return (
    <div className="flex flex-col h-full px-10 py-7">
      <div className="mb-5 pb-3 border-b-2" style={{ borderColor: `${primaryColor}40` }}>
        <h2 className="text-2xl font-bold text-slate-900">{slide.title}</h2>
      </div>
      <div className="flex-1 grid grid-cols-[1fr_auto_1fr] gap-0 items-center overflow-hidden">
        {/* Left column */}
        <div className="pr-6">
          <ul className="space-y-4">
            {(slide.content?.leftColumn ?? []).map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span
                  className="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: primaryColor }}
                />
                <span className="text-base text-slate-700 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        {/* Divider */}
        <div className="w-px self-stretch my-2" style={{ backgroundColor: `${primaryColor}30` }} />
        {/* Right column */}
        <div className="pl-6">
          <ul className="space-y-4">
            {(slide.content?.rightColumn ?? []).map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <span
                  className="mt-1.5 w-2.5 h-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: `${primaryColor}99` }}
                />
                <span className="text-base text-slate-700 leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function QuoteSlideLayout({ slide, primaryColor }: { slide: Slide; primaryColor: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full px-12 py-10">
      {/* Decorative quote mark */}
      <Quote
        className="w-16 h-16 mb-4 opacity-20"
        style={{ color: primaryColor }}
      />
      <blockquote className="text-xl sm:text-2xl font-medium italic text-slate-800 text-center leading-relaxed max-w-2xl mx-auto">
        &ldquo;{slide.content?.quote}&rdquo;
      </blockquote>
      {slide.content?.attribution && (
        <p className="mt-6 text-sm font-semibold tracking-wide uppercase text-slate-500 text-center">
          &mdash; {slide.content.attribution}
        </p>
      )}
      {slide.title && slide.title.toLowerCase() !== 'quote' && (
        <p className="mt-2 text-xs text-slate-400 text-center">{slide.title}</p>
      )}
    </div>
  );
}

function ImageTextSlideLayout({ slide, primaryColor }: { slide: Slide; primaryColor: string }) {
  return (
    <div className="flex flex-col h-full px-10 py-7">
      <div className="mb-4 pb-3 border-b-2" style={{ borderColor: `${primaryColor}40` }}>
        <h2 className="text-2xl font-bold text-slate-900">{slide.title}</h2>
      </div>
      <div className="flex-1 grid grid-cols-2 gap-6 overflow-hidden">
        {/* Image / placeholder */}
        <div className="flex items-center justify-center">
          {slide.content?.generatedImageUrl ? (
            <img
              src={slide.content.generatedImageUrl}
              alt={slide.content.imagePrompt || 'Slide image'}
              className="w-full h-full object-cover rounded-lg shadow-sm"
            />
          ) : (
            <div className="w-full h-full rounded-lg border-2 border-dashed border-slate-200 flex flex-col items-center justify-center bg-slate-50 p-4">
              <Image className="w-10 h-10 text-slate-300 mb-2" />
              {slide.content?.imagePrompt && (
                <p className="text-xs text-slate-400 text-center line-clamp-3">
                  {slide.content.imagePrompt}
                </p>
              )}
            </div>
          )}
        </div>
        {/* Text content */}
        <div className="flex flex-col justify-center">
          {slide.content?.mainText && (
            <p className="text-base text-slate-700 leading-relaxed mb-3">
              {slide.content.mainText}
            </p>
          )}
          {slide.content?.bullets && slide.content.bullets.length > 0 && (
            <ul className="space-y-2.5">
              {slide.content.bullets.map((bullet, idx) => (
                <li key={idx} className="flex items-start gap-2.5">
                  <span
                    className="mt-1.5 w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: primaryColor }}
                  />
                  <span className="text-sm text-slate-700">{bullet}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function SlideRenderer({ slide, primaryColor }: { slide: Slide; primaryColor: string }) {
  switch (slide.layout) {
    case 'title':
      return <TitleSlideLayout slide={slide} primaryColor={primaryColor} />;
    case 'content':
      return <ContentSlideLayout slide={slide} primaryColor={primaryColor} />;
    case 'bullets':
      return <BulletsSlideLayout slide={slide} primaryColor={primaryColor} />;
    case 'two-column':
      return <TwoColumnSlideLayout slide={slide} primaryColor={primaryColor} />;
    case 'quote':
      return <QuoteSlideLayout slide={slide} primaryColor={primaryColor} />;
    case 'image-text':
      return <ImageTextSlideLayout slide={slide} primaryColor={primaryColor} />;
    default:
      return <ContentSlideLayout slide={slide} primaryColor={primaryColor} />;
  }
}

// ─── Main Component ────────────────────────────────────────────────────────────

export function SlidesViewer({ slides, className = '' }: SlidesViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
  const [isAnimating, setIsAnimating] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const thumbnailActiveRef = useRef<HTMLButtonElement>(null);

  const { generateDocx } = useDocumentGenerator();
  const { generatePptx, isGenerating: isPptxGenerating } = usePptxGenerator();

  const currentSlide = slides.slides[currentIndex];
  const totalSlides = slides.slides.length;
  const primaryColor = slides.theme?.primaryColor || '#3b82f6';

  // ── Navigation ───────────────────────────────────────────────────────────

  const navigateTo = useCallback(
    (index: number) => {
      if (index < 0 || index >= totalSlides || index === currentIndex || isAnimating) return;
      setSlideDirection(index > currentIndex ? 'right' : 'left');
      setIsAnimating(true);
      // Small delay so the exit animation plays before we swap content
      setTimeout(() => {
        setCurrentIndex(index);
        setIsAnimating(false);
      }, 150);
    },
    [currentIndex, totalSlides, isAnimating],
  );

  const goToPrevious = useCallback(() => navigateTo(currentIndex - 1), [currentIndex, navigateTo]);
  const goToNext = useCallback(() => navigateTo(currentIndex + 1), [currentIndex, navigateTo]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      } else if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext, isFullscreen]);

  // Scroll active thumbnail into view
  useEffect(() => {
    thumbnailActiveRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  }, [currentIndex]);

  // ── Export Handlers ──────────────────────────────────────────────────────

  const handleExportDocx = async () => {
    setIsExporting(true);
    try {
      const sections: DocumentSection[] = slides.slides.map((slide, idx) => ({
        id: `slide-${idx + 1}`,
        name: `Slide ${idx + 1}: ${slide.title}`,
        type: slide.content?.bullets?.length ? ('list' as const) : ('text' as const),
        content: slide.content?.bullets?.length
          ? slide.content.bullets
          : slide.content?.mainText || slide.content?.quote || '',
      }));

      const documentContent: DocumentContent = {
        title: slides.title,
        subtitle: slides.subtitle,
        generatedAt: new Date().toISOString(),
        sections,
      };

      await generateDocx(documentContent);
      toast.success('Slides exported as DOCX!');
    } catch (error) {
      console.error('DOCX export failed:', error);
      toast.error('Failed to export slides');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportPptx = async () => {
    try {
      await generatePptx(slides);
      toast.success('PowerPoint exported successfully!');
    } catch (error) {
      console.error('PPTX export failed:', error);
      toast.error('Failed to export PowerPoint');
    }
  };

  // ── Empty state ──────────────────────────────────────────────────────────

  if (!slides.slides.length) {
    return (
      <div className={cn('flex items-center justify-center p-8 text-muted-foreground', className)}>
        <Presentation className="w-8 h-8 mr-3 opacity-50" />
        <span>No slides generated</span>
      </div>
    );
  }

  const isExportLoading = isExporting || isPptxGenerating;

  // ── Animation class ──────────────────────────────────────────────────────

  const slideAnimationClass = isAnimating
    ? slideDirection === 'right'
      ? 'opacity-0 translate-x-4'
      : 'opacity-0 -translate-x-4'
    : 'opacity-100 translate-x-0';

  // ── Fullscreen mode ──────────────────────────────────────────────────────

  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center">
        {/* Slide canvas — fullscreen */}
        <div className="flex-1 flex items-center justify-center w-full px-4 py-4">
          <div
            className={cn(
              'relative bg-white rounded-lg shadow-2xl overflow-hidden w-full max-w-5xl aspect-[16/9] transition-all duration-150',
              slideAnimationClass,
            )}
          >
            <SlideRenderer slide={currentSlide} primaryColor={primaryColor} />
            {/* Slide number */}
            <span className="absolute bottom-2 right-3 text-[10px] text-slate-400 font-mono">
              {currentIndex + 1}
            </span>
          </div>
        </div>

        {/* Bottom controls */}
        <div className="w-full max-w-5xl px-4 pb-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPrevious}
            disabled={currentIndex === 0}
            className="text-white hover:bg-white/10"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Previous
          </Button>

          <span className="text-sm text-white/70 font-mono">
            {currentIndex + 1} / {totalSlides}
          </span>

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={goToNext}
              disabled={currentIndex === totalSlides - 1}
              className="text-white hover:bg-white/10"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-1" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsFullscreen(false)}
              className="text-white hover:bg-white/10"
            >
              <Minimize className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Normal mode ──────────────────────────────────────────────────────────

  return (
    <TooltipProvider delayDuration={300}>
      <div ref={containerRef} className={cn('flex flex-col', className)}>
        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-3">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold truncate">{slides.title}</h3>
            <p className="text-sm text-muted-foreground">
              {totalSlides} slide{totalSlides !== 1 ? 's' : ''}
              {slides.subtitle && <span> &middot; {slides.subtitle}</span>}
            </p>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setIsFullscreen(true)}
                >
                  <Maximize className="w-4 h-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Fullscreen</TooltipContent>
            </Tooltip>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2" disabled={isExportLoading}>
                  {isExportLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  {isExportLoading ? 'Exporting...' : 'Export'}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleExportPptx} disabled={isPptxGenerating}>
                  <Presentation className="w-4 h-4 mr-2" />
                  PowerPoint (.pptx)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleExportDocx} disabled={isExporting}>
                  <FileText className="w-4 h-4 mr-2" />
                  Word Document (.docx)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* ── Slide Canvas + Navigation ───────────────────────────── */}
        <div className="flex items-center gap-2">
          {/* Previous button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 flex-shrink-0 rounded-full"
            onClick={goToPrevious}
            disabled={currentIndex === 0}
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>

          {/* Slide canvas */}
          <div className="flex-1 min-w-0">
            <div className="relative aspect-[16/9] bg-white rounded-lg shadow-md border border-slate-200 overflow-hidden">
              <div
                className={cn(
                  'absolute inset-0 transition-all duration-150 ease-in-out',
                  slideAnimationClass,
                )}
              >
                <SlideRenderer slide={currentSlide} primaryColor={primaryColor} />
              </div>
              {/* Slide number badge */}
              <span className="absolute bottom-2 right-3 text-[10px] text-slate-400 font-mono select-none">
                {currentIndex + 1}
              </span>
              {/* Layout badge */}
              <Badge
                variant="secondary"
                className="absolute top-2 right-3 text-[9px] uppercase tracking-wider opacity-60"
              >
                {currentSlide.layout}
              </Badge>
            </div>
          </div>

          {/* Next button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-10 w-10 flex-shrink-0 rounded-full"
            onClick={goToNext}
            disabled={currentIndex === totalSlides - 1}
          >
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* ── Slide counter ───────────────────────────────────────── */}
        <div className="flex items-center justify-center mt-2">
          <span className="text-xs text-muted-foreground font-mono">
            {currentIndex + 1} / {totalSlides}
          </span>
        </div>

        {/* ── Thumbnail strip ─────────────────────────────────────── */}
        <div className="mt-3">
          <ScrollArea className="w-full">
            <div className="flex gap-2 pb-2 px-0.5">
              {slides.slides.map((slide, idx) => (
                <button
                  key={idx}
                  ref={idx === currentIndex ? thumbnailActiveRef : undefined}
                  onClick={() => navigateTo(idx)}
                  className={cn(
                    'flex-shrink-0 w-28 h-16 rounded-md border-2 text-left transition-all overflow-hidden relative bg-white',
                    idx === currentIndex
                      ? 'border-primary ring-1 ring-primary/30 shadow-sm'
                      : 'border-slate-200 hover:border-slate-300',
                  )}
                >
                  {/* Mini accent bar for title slides */}
                  {slide.layout === 'title' && (
                    <div
                      className="absolute top-0 left-0 right-0 h-0.5"
                      style={{ backgroundColor: primaryColor }}
                    />
                  )}
                  <div className="p-1.5 h-full flex flex-col justify-between">
                    <p className="text-[9px] font-medium text-slate-700 line-clamp-2 leading-tight">
                      {slide.title}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-[8px] text-slate-400 font-mono">{idx + 1}</span>
                      <span className="text-[7px] text-slate-400 uppercase">{slide.layout}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>

        {/* ── Speaker Notes ───────────────────────────────────────── */}
        <div className="mt-3">
          <button
            onClick={() => setShowNotes(!showNotes)}
            className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            {showNotes ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
            Speaker Notes
          </button>
          {showNotes && (
            <div className="mt-2 p-3 bg-muted/50 rounded-lg border text-sm text-slate-700 leading-relaxed">
              {currentSlide.speakerNotes ? (
                <p className="whitespace-pre-line">{currentSlide.speakerNotes}</p>
              ) : (
                <p className="text-muted-foreground italic">No speaker notes for this slide.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
}

export default SlidesViewer;
